// src/components/tasks/TaskChat.tsx
import { useState, useEffect, useRef, useMemo } from "react"
import {
  MessageCircle,
  FileText,
  Download,
  Check,
  CheckCheck,
  Send,
  Paperclip,
  Search,
  User,
  Calendar as CalendarIcon,
  Info,
  X,
  Eye,
  Smile,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useAuthStore } from "@/store/authStore"
import { useNotificationStore } from "@/store/notificationStore"
import { db, storage } from "@/lib/firebase"
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  Timestamp,
  doc,
  updateDoc,
  increment,
  addDoc,
  serverTimestamp,
  setDoc,
  arrayUnion,
} from "firebase/firestore"
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import type { Task, Message } from "@/types/task"
import { toast } from "@/hooks/use-toast"
import { motion } from "framer-motion"
import { cn } from "@/lib/utils"

// === ДИЗАЙН-СЛОЙ ===
import { taskChatTheme, MessageBubble } from "./ui/TaskChatUI"

interface TaskChatProps {
  task: Task
}

const getTimestamp = (ts: any): string => {
  if (!ts) return ""
  const date = ts instanceof Timestamp ? ts.toDate() : new Date(ts)
  return date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })
}

const getInitials = (name: string) =>
  name?.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "??"

const formatDate = (dateStr: string) => {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })
}

const formatFullDate = (ts: any) => {
  if (!ts) return ""
  const date = ts instanceof Timestamp ? ts.toDate() : new Date(ts)
  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })
}

const commonEmojis = ["😀", "😂", "❤️", "👍", "👏", "🔥", "🙏", "😍", "🥳", "🎉", "👌", "🙌", "💪", "😎", "🤔"]

export function TaskChat({ task }: TaskChatProps) {
  const { user } = useAuthStore()
  const { markTaskAsRead } = useNotificationStore()

  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [messageText, setMessageText] = useState("")
  const [attachedFiles, setAttachedFiles] = useState<File[]>([])
  const [isSending, setIsSending] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [typingUser, setTypingUser] = useState<string | null>(null)
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null)
  const [editText, setEditText] = useState("")
  const [replyTo, setReplyTo] = useState<Message | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const lastMessageRef = useRef<HTMLDivElement>(null)
  const prevScrollHeightRef = useRef<number>(0)
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null)

  const currentUserId = user ? Number(user.id) : 0

  const canEditMessage = (msg: Message): boolean => {
    if (Number(msg.senderId) !== currentUserId) return false
    if (!msg.timestamp) return false
    const messageTime = msg.timestamp instanceof Timestamp
      ? msg.timestamp.toDate()
      : new Date(msg.timestamp)
    const now = new Date()
    const diffMs = now.getTime() - messageTime.getTime()
    const diffDays = diffMs / (1000 * 60 * 60 * 24)
    return diffDays <= 7
  }

  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = "auto"
      textarea.style.height = Math.min(textarea.scrollHeight, 180) + "px"
    }
  }, [messageText])

  useEffect(() => {
    if (!task?.id || !currentUserId) return

    const resetNotifications = async () => {
      try {
        await markTaskAsRead(task.id)
        await updateDoc(doc(db, "tasks", task.id), {
          [`unreadMessagesBy.${currentUserId}`]: 0,
        })
      } catch (err) {
        console.error("Ошибка сброса уведомлений:", err)
      }
    }

    resetNotifications()
  }, [task?.id, currentUserId, markTaskAsRead])

  useEffect(() => {
    if (!task?.id) {
      setMessages([])
      setLoading(false)
      return
    }

    setLoading(true)

    const messagesRef = collection(db, "tasks", task.id, "messages")
    const q = query(messagesRef, orderBy("timestamp", "asc"))

    const unsubscribe = onSnapshot(q, async (snap) => {
      const loaded = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Message))
      setMessages(loaded)
      setLoading(false)

      const container = scrollRef.current
      if (container) {
        const wasAtBottom = Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight) < 120
        if (wasAtBottom || prevScrollHeightRef.current === 0) {
          container.scrollTop = container.scrollHeight
        }
        prevScrollHeightRef.current = container.scrollHeight
      }
    })

    return () => unsubscribe()
  }, [task?.id])

  useEffect(() => {
    if (!task?.id || !user) return

    const typingRef = doc(db, "tasks", task.id, "meta", "typing")
    const unsubscribe = onSnapshot(typingRef, (snap) => {
      const data = snap.data()
      const typing = Object.entries(data || {}).filter(([k]) => Number(k) !== currentUserId)
      setTypingUser(typing.length > 0 ? (typing[0][1] as string) : null)
    })

    let timeout: NodeJS.Timeout
    const handleTyping = () => {
      clearTimeout(timeout)
      setDoc(typingRef, { [currentUserId]: user.name }, { merge: true })
      timeout = setTimeout(() => setDoc(typingRef, { [currentUserId]: null }, { merge: true }), 3000)
    }

    document.addEventListener("input", handleTyping, true)
    return () => {
      unsubscribe()
      document.removeEventListener("input", handleTyping, true)
      clearTimeout(timeout)
    }
  }, [task?.id, user, currentUserId])

  const handleMessageInteraction = (msg: Message) => {
    if (Number(msg.senderId) === currentUserId && canEditMessage(msg)) {
      setEditingMessageId(msg.id)
      setEditText(msg.text || "")
    } else {
      setReplyTo(msg)
    }
  }

  const saveEdit = async (messageId: string) => {
    if (!editText.trim()) return
    try {
      await updateDoc(doc(db, "tasks", task.id, "messages", messageId), {
        text: editText.trim(),
        edited: true,
        editedAt: serverTimestamp(),
      })
      setEditingMessageId(null)
      setEditText("")
      toast({ title: "Сообщение изменено" })
    } catch (e) {
      console.error("Ошибка редактирования:", e)
      toast({ title: "Ошибка", description: "Не удалось изменить", variant: "destructive" })
    }
  }

  const cancelEdit = () => {
    setEditingMessageId(null)
    setEditText("")
  }

  const cancelReply = () => setReplyTo(null)

  const insertEmoji = (emoji: string) => {
    const textarea = textareaRef.current
    if (!textarea) return
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const text = messageText
    const newText = text.substring(0, start) + emoji + text.substring(end)
    setMessageText(newText)
    setTimeout(() => {
      textarea.focus()
      textarea.selectionStart = textarea.selectionEnd = start + emoji.length
    }, 0)
  }

  const sendMessage = async () => {
    if (!user || (!messageText.trim() && attachedFiles.length === 0) || isSending || !task?.id) return
    setIsSending(true)

    try {
      const uploadedFiles: { name: string; url: string }[] = []

      for (const file of attachedFiles) {
        const fileRef = ref(storage, `tasks/${task.id}/messages/${Date.now()}_${file.name}`)
        await uploadBytes(fileRef, file)
        const url = await getDownloadURL(fileRef)
        uploadedFiles.push({ name: file.name, url })
      }

      const msg: any = {
        text: messageText.trim(),
        senderId: user.id,
        senderName: user.name,
        timestamp: serverTimestamp(),
        readBy: [currentUserId],
      }

      if (uploadedFiles.length > 0) {
        msg.files = uploadedFiles
        if (uploadedFiles.length === 1) msg.file = uploadedFiles[0]
      }

      if (replyTo) {
        msg.replyTo = {
          messageId: replyTo.id,
          text: replyTo.text.slice(0, 120) + (replyTo.text.length > 120 ? "..." : ""),
          senderName: replyTo.senderName
        }
      }

      await addDoc(collection(db, "tasks", task.id, "messages"), msg)

      const creatorId = Number(task.createdById || task.creatorId || task.createdBy || 0)

      const updates: Record<string, any> = {}

      if (task.assigneeId && Number(task.assigneeId) !== currentUserId) {
        updates[`unreadMessagesBy.${task.assigneeId}`] = increment(1)
      }

      task.coAssigneeIds?.forEach((id: number) => {
        if (id !== currentUserId) updates[`unreadMessagesBy.${id}`] = increment(1)
      })

      task.observerIds?.forEach((id: number) => {
        if (id !== currentUserId) updates[`unreadMessagesBy.${id}`] = increment(1)
      })

      if (creatorId && creatorId !== currentUserId) {
        updates[`unreadMessagesBy.${creatorId}`] = increment(1)
      }

      if (Object.keys(updates).length > 0) {
        await updateDoc(doc(db, "tasks", task.id), updates)
      }

      setMessageText("")
      setAttachedFiles([])
      setReplyTo(null)
      if (fileInputRef.current) fileInputRef.current.value = ""

      toast({ title: "Сообщение отправлено" })

      setTimeout(() => {
        lastMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }, 300)
    } catch (e) {
      console.error("Ошибка отправки сообщения:", e)
      toast({ title: "Ошибка", description: "Не удалось отправить", variant: "destructive" })
    } finally {
      setIsSending(false)
    }
  }

  const filteredMessages = useMemo(() => {
    return searchQuery
      ? messages.filter((m) => m.text?.toLowerCase().includes(searchQuery.toLowerCase()))
      : messages
  }, [messages, searchQuery])

  const participantsText = [task.assignee, ...(task.coAssignees || [])].filter(Boolean).join(", ")

  const chatFiles = useMemo(() => {
    return messages
      .filter((msg) => (msg.files?.length || msg.file) && msg.timestamp)
      .sort((a, b) => {
        const timeA = a.timestamp instanceof Timestamp ? a.timestamp.toMillis() : new Date(a.timestamp).getTime()
        const timeB = b.timestamp instanceof Timestamp ? b.timestamp.toMillis() : new Date(b.timestamp).getTime()
        return timeB - timeA
      })
      .flatMap((msg) => {
        if (msg.files?.length) return msg.files.map(f => ({ ...f, timestamp: msg.timestamp }))
        if (msg.file) return [{ ...msg.file, timestamp: msg.timestamp }]
        return []
      })
  }, [messages])

  const totalFilesCount = (task.file ? 1 : 0) + chatFiles.length

  const forceDownload = async (url: string, fileName: string) => {
    try {
      const response = await fetch(url, { method: 'GET', mode: 'cors' })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (err) {
      console.error('Ошибка скачивания:', err)
      window.open(url, '_blank')
    }
  }

  return (
    <div className={taskChatTheme.mainContainer}>
      <div className={taskChatTheme.header}>
        <div className="flex items-center gap-4">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-md">
            <MessageCircle className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">
              {task.title}
            </h2>
            <p className="text-sm lg:text-base text-purple-200/90 mt-0.5">
              Участники: {participantsText || "не указаны"}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className={taskChatTheme.sidebar}>
          <div className="space-y-5">
            <div className={taskChatTheme.sidebarSection}>
              <div className="flex items-center gap-2 text-purple-300 text-sm lg:text-base mb-2">
                <Info className="h-3.5 w-3.5" />
                <span className="font-medium">Описание задачи</span>
              </div>
              <p className="text-purple-100 text-sm lg:text-base leading-relaxed">
                {task.description || "Описание отсутствует"}
              </p>
            </div>

            <div className={taskChatTheme.sidebarSection}>
              <div className="flex items-center gap-2 text-purple-300 text-sm lg:text-base mb-2">
                <User className="h-3.5 w-3.5" />
                <span className="font-medium">Поставил задачу</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className={taskChatTheme.avatarOther}>
                    {getInitials(task.createdBy || "")}
                  </AvatarFallback>
                </Avatar>
                <span className="text-white text-sm lg:text-base font-medium truncate">
                  {task.createdBy || "—"}
                </span>
              </div>
            </div>

            <div className={taskChatTheme.sidebarSection}>
              <div className="flex items-center gap-2 text-purple-300 text-sm lg:text-base">
                <User className="h-3.5 w-3.5" />
                <span className="font-medium">Исполнитель</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className={taskChatTheme.avatarOwn}>
                    {getInitials(task.assignee || "")}
                  </AvatarFallback>
                </Avatar>
                <span className="text-white text-sm lg:text-base font-medium truncate">
                  {task.assignee || "—"}
                </span>
              </div>
            </div>

            {task.coAssignees && task.coAssignees.length > 0 && (
              <div className={taskChatTheme.sidebarSection}>
                <div className="flex items-center gap-2 text-purple-300 text-sm lg:text-base">
                  <User className="h-3.5 w-3.5" />
                  <span className="font-medium">Соисполнители</span>
                </div>
                <div className="space-y-2">
                  {task.coAssignees.map((co, idx) => (
                    <div key={idx} className="flex items-center gap-2.5">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-gradient-to-br from-pink-600 to-purple-600 text-white text-xs font-bold">
                          {getInitials(co)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-white text-sm lg:text-base font-medium truncate">{co}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {task.observers && task.observers.length > 0 && (
              <div className={taskChatTheme.sidebarObserverSection}>
                <div className="flex items-center gap-2 text-amber-300 text-sm lg:text-base">
                  <Eye className="h-3.5 w-3.5" />
                  <span className="font-medium">Наблюдатели</span>
                </div>
                <div className="space-y-2">
                  {task.observers.map((obs, idx) => (
                    <div key={idx} className="flex items-center gap-2.5">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-gradient-to-br from-amber-600 to-orange-600 text-white text-xs font-bold">
                          {getInitials(obs)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-white text-sm lg:text-base font-medium truncate">{obs}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className={taskChatTheme.sidebarSection}>
              <div className="flex items-center gap-2 text-purple-300 text-sm lg:text-base mb-2">
                <CalendarIcon className="h-3.5 w-3.5" />
                <span className="font-medium">Дата завершения</span>
              </div>
              <p className="text-purple-100 text-sm lg:text-base">{formatDate(task.deadline || "")}</p>
            </div>

            <div className={taskChatTheme.documentsBlock}>
              <div className="text-white text-lg lg:text-xl font-bold mb-5">
                Прикреплённые документы ({totalFilesCount})
              </div>

              {task.file && (
                <div className="mb-8">
                  <div className="text-purple-200 text-sm lg:text-base mb-3 font-semibold">Файл от создателя задачи</div>
                  <div className="flex items-center justify-between bg-gradient-to-br from-fuchsia-700/95 to-purple-800/85 border border-fuchsia-600/50 rounded-3xl p-4 hover:brightness-105 transition-all duration-200">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="bg-gradient-to-br from-fuchsia-600 to-purple-600 rounded-2xl p-3 shadow-md flex-shrink-0">
                        <FileText className="h-6 w-6 text-white" />
                      </div>
                      <span className="text-white text-base lg:text-lg font-medium truncate flex-1 min-w-0">
                        {task.file.name}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 text-white hover:bg-white/15 rounded-2xl transition-colors flex-shrink-0"
                      onClick={() => forceDownload(task.file!.url, task.file!.name)}
                    >
                      <Download className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              )}

              <div>
                <div className="text-purple-200 text-sm lg:text-base mb-3 font-semibold">Прикреплённые файлы чата</div>

                {chatFiles.length > 0 ? (
                  <div className="space-y-4">
                    {chatFiles.map((file, idx) => {
                      const date = file.timestamp instanceof Timestamp
                        ? file.timestamp.toDate()
                        : new Date(file.timestamp)
                      const shortDate = date.toLocaleDateString("ru-RU", {
                        day: "numeric",
                        month: "short",
                      })

                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-between bg-gradient-to-br from-indigo-950/95 to-purple-950/90 border border-indigo-700/50 rounded-3xl p-4 hover:brightness-105 transition-all duration-200 gap-4"
                        >
                          <div className="flex items-center gap-4 flex-1 min-w-0">
                            <div className="bg-gradient-to-br from-indigo-700 to-purple-800 rounded-2xl p-3 shadow-md flex-shrink-0">
                              <FileText className="h-6 w-6 text-white" />
                            </div>
                            <div className="flex flex-col flex-1 min-w-0">
                              <span className="text-white text-base lg:text-lg font-medium break-all line-clamp-2" title={file.name}>
                                {file.name}
                              </span>
                              <span className="text-indigo-300 text-sm lg:text-base opacity-90 mt-1">
                                {shortDate}
                              </span>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 text-indigo-300 hover:text-white hover:bg-white/15 rounded-2xl transition-colors flex-shrink-0"
                            onClick={() => forceDownload(file.url, file.name)}
                          >
                            <Download className="h-5 w-5" />
                          </Button>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-purple-400/80 text-sm lg:text-base italic text-center py-6">
                    Пока нет прикреплённых файлов в чате
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={taskChatTheme.chatArea}>
          <div className="p-5 border-b border-purple-300/30 bg-purple-950/30">
            <div className="relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-purple-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Поиск по сообщениям..."
                className={taskChatTheme.searchBar}
              />
            </div>
          </div>

          <div className="flex-1 px-6 py-8 overflow-y-auto no-scrollbar" ref={scrollRef}>
            {loading ? (
              <div className="flex justify-center items-center h-full text-purple-400 text-base">
                Загрузка сообщений...
              </div>
            ) : messages.length === 0 ? (
              <div className={taskChatTheme.emptyState}>
                <MessageCircle className="h-16 w-16 opacity-40" />
                <div className="text-center">
                  <p className="text-slate-300 text-xl font-medium">Сообщений пока нет</p>
                  <p className="text-slate-500 mt-3 text-base">Напиши первым!</p>
                </div>
              </div>
            ) : (
              filteredMessages.map((msg, index) => {
                const isOwn = Number(msg.senderId) === currentUserId
                const prevMsg = index > 0 ? filteredMessages[index - 1] : null
                const isEditing = editingMessageId === msg.id
                const isLast = index === filteredMessages.length - 1
                const currentDay = formatFullDate(msg.timestamp)
                const prevDay = prevMsg ? formatFullDate(prevMsg.timestamp) : null
                const totalParticipants = 1 + (task.coAssignees?.length || 0) + (task.observers?.length || 0) + 1
                const readCount = msg.readBy?.length || 1
                const allRead = readCount >= totalParticipants
                const isFirstInGroup = !prevMsg || Number(prevMsg.senderId) !== Number(msg.senderId)

                return (
                  <div ref={isLast ? lastMessageRef : null} key={msg.id || index}>
                    {prevDay && currentDay !== prevDay && (
                      <div className="w-full my-8 text-center">
                        <span className={taskChatTheme.dateSeparator}>
                          {currentDay}
                        </span>
                      </div>
                    )}

                    <motion.div
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className={cn(
                        "flex items-end mb-1.5 relative group",
                        isOwn ? "justify-end" : "justify-start"
                      )}
                    >
                      {!isOwn && (
                        <Avatar className="mb-1 mr-3 h-8 w-8 flex-shrink-0">
                          <AvatarFallback className={taskChatTheme.avatarOther}>
                            {getInitials(msg.senderName)}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      {!isOwn || <div className="w-11 flex-shrink-0" />}

                      <MessageBubble isOwn={isOwn} isFirstInGroup={isFirstInGroup}>
                        {msg.replyTo && (
                          <div className="mb-3 -mx-4 -mt-3 pt-3 px-4 pb-2 bg-slate-900/60 rounded-t-2xl border-l-4 border-purple-500 cursor-pointer hover:bg-slate-900/80 transition-colors">
                            <div className="text-sm lg:text-base font-semibold text-purple-300 mb-1">
                              {msg.replyTo.senderName}
                            </div>
                            <div className="text-sm lg:text-base text-purple-100 line-clamp-2 break-words">
                              {msg.replyTo.text}
                            </div>
                          </div>
                        )}

                        {isEditing ? (
                          <div className="space-y-3 -mx-1 -my-1">
                            <textarea
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              autoFocus
                              rows={Math.min(8, editText.split('\n').length + 2)}
                              className={taskChatTheme.editingTextarea}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                  e.preventDefault()
                                  saveEdit(msg.id)
                                }
                                if (e.key === "Escape") {
                                  cancelEdit()
                                }
                              }}
                              placeholder="Редактируйте сообщение..."
                            />
                            <div className="flex justify-end gap-3 text-sm pt-1">
                              <button type="button" onClick={cancelEdit} className="px-4 py-1.5 text-purple-300 hover:text-purple-100 hover:bg-purple-900/40 rounded-md transition-colors">
                                Отмена
                              </button>
                              <button
                                type="button"
                                onClick={() => saveEdit(msg.id)}
                                disabled={!editText.trim() || editText.trim() === msg.text?.trim()}
                                className={cn(
                                  "px-5 py-1.5 rounded-md font-medium transition-colors",
                                  editText.trim() && editText.trim() !== msg.text?.trim()
                                    ? "bg-purple-600 hover:bg-purple-700 text-white"
                                    : "bg-purple-800/50 text-purple-300 cursor-not-allowed"
                                )}
                              >
                                Сохранить
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {isFirstInGroup && !isOwn && (
                              <div className="text-base lg:text-lg font-medium text-purple-200/90 mb-1.5">
                                {msg.senderName}
                              </div>
                            )}
                            <p className="break-words whitespace-pre-wrap text-lg lg:text-xl leading-relaxed">{msg.text}</p>
                            {msg.edited && <span className="text-xs text-purple-300/70 italic ml-1">(изменено)</span>}

                            {(msg.files || (msg.file ? [msg.file] : [])).map((f: any, i: number) => (
                              <div key={i} className={taskChatTheme.fileAttachment}>
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <FileText className="h-5 w-5 text-purple-300 flex-shrink-0" />
                                  <span className="text-sm lg:text-base font-medium text-white break-all line-clamp-2" title={f.name}>
                                    {f.name}
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-purple-300 hover:text-white hover:bg-purple-700/50 flex-shrink-0"
                                  onClick={() => forceDownload(f.url, f.name)}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}

                            <div className="mt-2 flex items-center justify-end gap-2 text-sm lg:text-base opacity-85">
                              <span>{getTimestamp(msg.timestamp)}</span>
                              {isOwn && (
                                <span className="ml-1 flex items-center gap-1">
                                  {allRead ? (
                                    <CheckCheck className="h-4 w-4 text-cyan-300" />
                                  ) : (
                                    <Check className="h-4 w-4 text-slate-400" />
                                  )}
                                  <span className={cn(
                                    "text-sm lg:text-base font-medium",
                                    allRead ? "text-cyan-300" : "text-slate-400"
                                  )}>
                                    {readCount}/{totalParticipants}
                                  </span>
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </MessageBubble>

                      {isOwn && (
                        <Avatar className="mb-1 ml-3 h-8 w-8 flex-shrink-0">
                          <AvatarFallback className={taskChatTheme.avatarOwn}>
                            {getInitials(msg.senderName)}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      {isOwn || <div className="w-11 flex-shrink-0" />}
                    </motion.div>
                  </div>
                )
              })
            )}

            {typingUser && <div className={taskChatTheme.typingIndicator}>{typingUser} печатает...</div>}
          </div>

          <div className={taskChatTheme.inputBar}>
            {attachedFiles.length > 0 && (
              <div className="px-6 pt-4 pb-2 flex flex-wrap gap-2 border-t border-purple-700/30">
                {attachedFiles.map((file, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="bg-purple-900/60 text-purple-100 border-purple-600/60 flex items-center gap-2 px-4 py-2"
                  >
                    <span className="truncate max-w-[180px]">{file.name}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 rounded-full hover:bg-purple-800/60 p-0"
                      onClick={() => setAttachedFiles(prev => prev.filter((_, i) => i !== index))}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            )}

            <div className="flex items-center gap-4">
              <label className="cursor-pointer p-3 hover:bg-purple-900/40 rounded-full transition-colors">
                <input
                  type="file"
                  multiple
                  className="hidden"
                  ref={fileInputRef}
                  onChange={(e) => {
                    if (e.target.files) setAttachedFiles(prev => [...prev, ...Array.from(e.target.files!)])
                  }}
                />
                <Paperclip className="h-7 w-7 text-purple-300 hover:text-purple-100 transition" />
              </label>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" className={taskChatTheme.emojiButton}>
                    <Smile className="!h-9 !w-9 text-purple-300 hover:text-purple-100" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className={taskChatTheme.emojiPopoverContent} align="start">
                  <div className="grid grid-cols-8 gap-4">
                    {commonEmojis.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => insertEmoji(emoji)}
                        className={taskChatTheme.emojiItem}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              <Textarea
                ref={textareaRef}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Напиши сообщение"
                className="flex-1 bg-purple-950/60 border-purple-300/40 text-white placeholder:text-purple-400/70 rounded-3xl px-6 py-3 text-lg lg:text-xl shadow-inner focus-visible:ring-purple-500 min-h-[44px] max-h-[180px] resize-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault()
                    sendMessage()
                  }
                }}
                disabled={isSending}
              />

              <Button
                onClick={sendMessage}
                disabled={isSending || (!messageText.trim() && attachedFiles.length === 0)}
                className="rounded-3xl h-12 w-12 p-0 transition-all flex items-center justify-center shadow-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                {isSending ? (
                  <div className="h-7 w-7 animate-spin rounded-full border-3 border-white border-t-transparent" />
                ) : (
                  <Send className="h-7 w-7" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <style jsx global>{`
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  )
}